import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { HasAccessDirective } from '@sharedModule/directives/has-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ConfirmationCommonDialogData } from '@sharedModule/models/confirmation-dialog.model';
import { CountryInfo } from '@sharedModule/models/country.model';
import { DestinationCountry } from '@sharedModule/models/destination-country.model';
import { ResponseIdentity } from '@sharedModule/models/server-response-identity.model';
import { ColumnPipe } from '@sharedModule/pipe/column/column.pipe';
import { ConfirmationDialogService } from '@sharedModule/services/confirmation-dialog/confirmation-dialog.service';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { Subject, takeUntil } from 'rxjs';
import { TRANSACTION_SETTINGS } from '../../constant/transaction-settings.constant';
import { TransactionSettingsService } from '../../services/transaction-settings.service';
import { PartnerTransactionSettingsInterface } from '../../types/transaction-settings';
import { UpsertPartnerTransactionSettingsComponent } from '../upsert-partner-transaction-settings/upsert-partner-transaction-settings.component';

interface SelectEvent {
  value: string | number;
}

@Component({
  selector: 'app-partner-transaction-settings',
  standalone: true,
  imports: [
    ColumnPipe,
    InputModule,
    CommonModule,
    MaterialModule,
    HasAccessDirective,
    ReactiveFormsModule,
  ],
  templateUrl: './partner-transaction-settings.component.html',
  styleUrl: './partner-transaction-settings.component.scss',
})
export class PartnerTransactionSettingsComponent implements OnInit, OnDestroy {
  public readonly tabs = TRANSACTION_SETTINGS;
  public readonly attrConstant = ATTRIBUTES_CONSTANT.settings.transaction;

  displayedColumns: string[] = ['name', 'action'];
  dataSource!: MatTableDataSource<PartnerTransactionSettingsInterface>;
  transactionSettings!: PartnerTransactionSettingsInterface;
  currentTabLabel: string = this.tabs[0].code;
  filterForm!: FormGroup;

  isLoading = false;

  sourceCountryList: CountryInfo[] = [];
  destCountryList: CountryInfo[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private dialogRef: MatDialog,
    private messageHandlerService: MessageHandlerService,
    private confirmationDialogService: ConfirmationDialogService,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    this.initFilterForm();
    this.loadSourceCountries();
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      [this.attrConstant.srcCountry.key]: ['', Validators.required],
      [this.attrConstant.destCountry.key]: ['', Validators.required],
    });
  }

  private loadSourceCountries(): void {
    this.store
      .select(selectSourceCountry)
      .pipe(takeUntil(this.destroy$))
      .subscribe(countries => {
        if (countries?.length > 0) {
          this.sourceCountryList = [...countries];
          if (countries.length > 0) {
            const defaultCountry = countries[0];
            this.filterForm.patchValue({
              [this.attrConstant.srcCountry.key]: defaultCountry.countryCode,
            });
            this.onSourceCountryChange({ value: defaultCountry.countryCode });
          }
        }
      });
  }

  onSourceCountryChange(event: SelectEvent): void {
    this.resetFormControl(this.attrConstant.destCountry.key);

    if (event.value === '') {
      this.messageHandlerService.show('Source country cannot be empty.');
      return;
    }

    this.loadDestinationCountries(event.value as string);
  }

  onDestCountryChange(event: SelectEvent): void {
    if (event.value === '') {
      this.messageHandlerService.show('Destination country cannot be empty.');
      return;
    }

    this.getTransactionSettings();
  }

  private resetFormControl(controlName: string): void {
    this.filterForm.get(controlName)?.reset('');
  }

  private loadDestinationCountries(sourceCountry: string): void {
    this.transactionSettingsService
      .getDestinationCountryDropdown({
        src_country: sourceCountry,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: this.handleDestinationCountriesResponse.bind(this),
      });
  }

  private handleDestinationCountriesResponse(
    response: ResponseIdentity<DestinationCountry>
  ): void {
    if (response.success) {
      if (
        !response.response?.destination_currency ||
        response.response.destination_currency.length === 0
      ) {
        this.messageHandlerService.show(
          'No destination countries available for the selected source country'
        );
        this.destCountryList = [];
        return;
      }

      const countries = response.response.destination_currency;
      this.destCountryList = [...countries];

      if (countries.length > 0) {
        const defaultDestCountry = countries[0];
        this.filterForm.patchValue({
          [this.attrConstant.destCountry.key]: defaultDestCountry.countryCode,
        });
        this.getTransactionSettings();
      }
    } else {
      this.messageHandlerService.show('Failed to load destination countries');
    }
  }

  getTransactionSettings(): void {
    const formValue = this.filterForm.value;
    this.transactionSettingsService
      .getPartnerTransactionSettings({
        src_country: formValue[this.attrConstant.srcCountry.key],
        dest_country: formValue[this.attrConstant.destCountry.key],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: x => {
          if (x.success && x.response) {
            this.transactionSettings = x.response;
            this.dataSource = new MatTableDataSource(
              x.response[this.currentTabLabel] || []
            );
          } else {
            this.messageHandlerService.show(
              'Failed to load transaction settings'
            );
            this.dataSource =
              new MatTableDataSource<PartnerTransactionSettingsInterface>([]);
          }
        },
      });
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.currentTabLabel = this.tabs[event.index].code;
    this.dataSource = new MatTableDataSource(
      this.transactionSettings[this.currentTabLabel] || []
    );
  }

  addTransactionSettings(): void {
    const dialogRef = this.dialogRef.open(
      UpsertPartnerTransactionSettingsComponent,
      {
        disableClose: true,
        data: {
          action: 'Add',
          column: this.currentTabLabel,
          sourceCountryList: this.sourceCountryList,
        },
      }
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) this.getTransactionSettings();
      });
  }

  onEdit(data: PartnerTransactionSettingsInterface): void {
    const dialogRef = this.dialogRef.open(
      UpsertPartnerTransactionSettingsComponent,
      {
        disableClose: true,
        data: {
          isUpdate: true,
          column: this.currentTabLabel,
          id: this.transactionSettings.id,
          sourceCountryList: this.sourceCountryList,
          selectedTransactionSetting: {
            data: data,
            source: this.transactionSettings.source_country,
            destination: this.transactionSettings.destination_country,
          },
        },
      }
    );
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) this.getTransactionSettings();
      });
  }

  async onDelete(data: PartnerTransactionSettingsInterface): Promise<void> {
    const alertCommonDialogData: ConfirmationCommonDialogData = {
      title: '',
      message: `Are you sure you're going to delete this?`,
      submitButtonText: 'Confirm',
    };
    const isConfirmed =
      await this.confirmationDialogService.openConfirmationDialog(
        alertCommonDialogData
      );
    if (isConfirmed) {
      this.transactionSettingsService
        .deletePartnerTransactionSetting({
          id: this.transactionSettings.id,
          column: this.currentTabLabel,
          code: data['code'],
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: x => {
            if (x.success) {
              this.messageHandlerService.show(
                'Transaction setting deleted successfully'
              );
              this.getTransactionSettings();
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
